"use client";

import { useEffect } from "react";
import { useSession, signOut } from "next-auth/react";
import axios from "axios";
import { useZStore } from "@/utils/z-store";
import { useRouter } from "next/navigation";

/**
 * Component that checks if the user needs to verify their email
 * If user is unverified and account is older than 1 hour:
 * - Signs out the user
 * - Redirects to homepage
 * - Shows verification modal
 */
export default function EmailVerificationChecker() {
  const { data: session } = useSession();
  const { data, actions } = useZStore();
  const router = useRouter();

  useEffect(() => {
    const checkEmailVerification = async () => {
      if (session?.user?.email && data.userId) {
        try {
          const response = await axios.get("/api/users/verification-status");
          const { isVerified, createdAt } = response.data;

          // Only check for users who signed up with credentials (not Google)
          // Google users are automatically verified
          if (!isVerified && createdAt) {
            const accountAge = Date.now() - new Date(createdAt).getTime();
            const oneHourInMs = 60 * 60 * 1000; // 1 hour

            // If account is older than 1 hour and still unverified
            if (accountAge > oneHourInMs) {
              // Check if user has already been logged out in this session
              const hasBeenLoggedOut = sessionStorage.getItem(
                "loggedOutForVerification"
              );

              if (!hasBeenLoggedOut) {
                // Set the email for the modal before signing out
                actions.setPendingVerificationEmail(session.user.email);
                
                // Mark that we've logged out this user
                sessionStorage.setItem("loggedOutForVerification", "true");
                
                // Sign out the user
                await signOut({ redirect: false });
                
                // Reset store
                actions.resetStore();
                
                // Redirect to homepage with sign-up modal (which handles verification)
                router.push("/?modal=sign-up");
              }
            }
          }
        } catch (error) {
          console.error("Error checking verification status:", error);
        }
      }
    };

    checkEmailVerification();
  }, [session, data.userId, actions, router]);

  // This component doesn't render anything
  return null;
}
